import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Loader2, CheckCircle2, Trophy, LogOut, Delete, AlertCircle, Plus, Minus, X, ClipboardList, AlertTriangle } from 'lucide-react';
import { getRoundName, isKnockoutStageMatch as isKnockoutMatch } from '../utils/tournamentRoundName';
import { buildPlayerById, resolveMatchSide } from '../utils/tournamentPlayers';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'referee_session_v1';
const INDIVIDUAL_FORMATS = new Set(['americano', 'mexicano', 'mixed_americano', 'king_of_court']);
const FORMAT_LABELS = {
  knockout: 'Knockout',
  double_elimination: 'Double Elim',
  round_robin: 'Round Robin',
  group_knockout: 'Groups + KO',
  americano: 'Americano',
  mexicano: 'Mexicano',
  mixed_americano: 'Mixed Americano',
  king_of_court: 'King of Court',
};

const sessionLoad = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (new Date(s.expires_at_iso) <= new Date()) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return s;
  } catch { return null; }
};
const sessionSave = (s) => localStorage.setItem(SESSION_KEY, JSON.stringify(s));
const sessionClear = () => localStorage.removeItem(SESSION_KEY);

const apiFetch = (path, opts = {}) =>
  fetch(`${API_BASE}/api/tournaments${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
  });

// ─────────────────────────────────────────────────────────────
// Root
// ─────────────────────────────────────────────────────────────
const RefereePortal = () => {
  const [session, setSession] = useState(sessionLoad());
  return (
    <div className="min-h-screen bg-slate-50" data-testid="referee-portal">
      {session ? (
        <RefereeDashboard session={session} onLogout={() => { sessionClear(); setSession(null); }} />
      ) : (
        <PinScreen onAuthed={(s) => { sessionSave(s); setSession(s); }} />
      )}
    </div>
  );
};
export default RefereePortal;


// ─────────────────────────────────────────────────────────────
// PIN entry screen
// ─────────────────────────────────────────────────────────────
const PinScreen = ({ onAuthed }) => {
  const [digits, setDigits] = useState(['', '', '', '']);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const press = (d) => {
    setError(null);
    setDigits((cur) => {
      const idx = cur.findIndex((x) => x === '');
      if (idx === -1) return cur;
      const next = [...cur]; next[idx] = String(d);
      return next;
    });
  };
  const back = () => {
    setError(null);
    setDigits((cur) => {
      const lastFilled = cur.map(Boolean).lastIndexOf(true);
      if (lastFilled === -1) return cur;
      const next = [...cur]; next[lastFilled] = '';
      return next;
    });
  };
  const clearDigits = () => setDigits(['', '', '', '']);
  const reset = () => { clearDigits(); setError(null); };

  const pin = digits.join('');
  const ready = pin.length === 4;

  const submit = useCallback(async () => {
    if (!ready) return;
    setSubmitting(true); setError(null);
    try {
      const r = await apiFetch('/referee/auth', {
        method: 'POST', body: JSON.stringify({ pin }),
      });
      if (r.status === 401) {
        setError('No active tournament found for this PIN');
        clearDigits();
        return;
      }
      if (!r.ok) {
        setError('Login failed. Please try again.');
        clearDigits();
        return;
      }
      const data = await r.json();
      onAuthed(data);
    } catch (e) {
      setError('Network error — check connection');
    } finally {
      setSubmitting(false);
    }
  }, [pin, ready, onAuthed]);

  // Auto-submit when 4 digits entered
  useEffect(() => {
    if (ready && !submitting) submit();
    // eslint-disable-next-line
  }, [ready]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 max-w-md mx-auto">
      <div className="text-center mb-8">
        <Trophy className="h-14 w-14 text-blue-600 mx-auto mb-3" />
        <h1 className="text-3xl font-bold text-slate-900">Referee Portal</h1>
        <p className="text-slate-500 mt-1">Enter your 4-digit tournament PIN</p>
      </div>

      <div className="flex gap-3 mb-3">
        {digits.map((d, i) => (
          <div key={i}
            className={`w-16 h-20 rounded-2xl border-2 flex items-center justify-center text-3xl font-bold
              ${d ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-300 bg-white text-slate-300'}`}
            data-testid={`pin-digit-${i}`}>
            {d || '•'}
          </div>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl px-4 py-3 text-sm flex items-center gap-2 mb-2" data-testid="pin-error">
          <AlertCircle className="h-4 w-4 shrink-0" /> {error}
        </div>
      )}
      {submitting && (
        <div className="text-blue-600 text-sm flex items-center gap-2 mb-2"><Loader2 className="h-4 w-4 animate-spin" /> Checking PIN…</div>
      )}

      {/* Numpad */}
      <div className="grid grid-cols-3 gap-3 w-full mt-6">
        {[1,2,3,4,5,6,7,8,9].map(n => (
          <button key={n} onClick={() => press(n)} disabled={submitting}
            className="h-16 rounded-2xl bg-white border border-slate-200 text-2xl font-semibold text-slate-800 hover:bg-slate-100 active:scale-95 transition"
            data-testid={`pin-key-${n}`}>{n}</button>
        ))}
        <button onClick={reset} disabled={submitting}
          className="h-16 rounded-2xl bg-slate-100 text-slate-500 text-sm font-semibold hover:bg-slate-200 active:scale-95"
          data-testid="pin-key-clear">Clear</button>
        <button onClick={() => press(0)} disabled={submitting}
          className="h-16 rounded-2xl bg-white border border-slate-200 text-2xl font-semibold text-slate-800 hover:bg-slate-100 active:scale-95"
          data-testid="pin-key-0">0</button>
        <button onClick={back} disabled={submitting}
          className="h-16 rounded-2xl bg-slate-100 text-slate-500 font-semibold hover:bg-slate-200 active:scale-95 flex items-center justify-center"
          data-testid="pin-key-back"><Delete className="h-5 w-5" /></button>
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────
// Authenticated referee dashboard
// ─────────────────────────────────────────────────────────────
const RefereeDashboard = ({ session, onLogout }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch('/referee/tournament', { headers: { 'x-referee-token': session.token } });
      if (r.status === 401) {
        // Session expired
        sessionClear();
        onLogout();
        return;
      }
      if (!r.ok) throw new Error('Failed to load tournament state');
      const data = await r.json();
      setState(data);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [session.token, onLogout]);

  useEffect(() => {
    load();
    // Auto-refresh every 15 seconds so referees see admin-triggered round advances
    pollRef.current = setInterval(load, 15000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  }
  if (error) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-2" />
        <p className="text-slate-800">{error}</p>
        <button onClick={load} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg">Retry</button>
      </div>
    );
  }

  const tnm = state.tournament;
  const isCompleted = tnm.status === 'completed';
  const allComplete = state.all_complete && !isCompleted;
  const matches = state.matches || [];
  const playerById = buildPlayerById(state.teams || []);
  const scoredCount = matches.filter(m => m.status === 'completed').length;

  return (
    <div className="max-w-md mx-auto" data-testid="referee-dashboard">
      <Header tnm={tnm} onLogout={onLogout} groupStageOpen={state.group_stage_open} />

      {isCompleted ? (
        <CompletedBanner />
      ) : allComplete ? (
        <AllCompleteBanner />
      ) : (
        <ProgressBar scored={scoredCount} total={matches.length} round={state.current_round} tnm={tnm} sampleMatch={matches[0]} groupStageOpen={state.group_stage_open} />
      )}

      {!isCompleted && (
        <div className="px-4 pb-24 space-y-6">
          {Object.entries(groupByCourt(matches)).map(([court, ms]) => (
            <CourtSection
              key={court}
              court={court}
              matches={ms}
              format={tnm.format}
              pointsPerMatch={tnm.points_per_match || 24}
              tournament={tnm}
              tiebreakerEnabled={isTiebreakerEnabled(tnm)}
              token={session.token}
              playerById={playerById}
              onScored={load}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Header = ({ tnm, onLogout, groupStageOpen }) => (
  <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between" data-testid="referee-header">
    <div className="flex-1 min-w-0">
      <h1 className="text-lg font-bold text-slate-900 truncate" data-testid="referee-tnm-title">{tnm.title}</h1>
      <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
        <span className="capitalize">{tnm.sport}</span>
        <span>·</span>
        <span>{FORMAT_LABELS[tnm.format] || tnm.format}</span>
        {(groupStageOpen || tnm.current_round > 0) && (
          <>
            <span>·</span>
            <span className="font-medium text-blue-700" data-testid="referee-round-label">
              {groupStageOpen ? 'Group Stage' : getRoundName(tnm, tnm.current_round)}
            </span>
          </>
        )}
      </div>
    </div>
    <button onClick={onLogout} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" aria-label="Sign out" data-testid="referee-logout">
      <LogOut className="h-5 w-5" />
    </button>
  </header>
);

const CompletedBanner = () => (
  <div className="m-4 p-6 bg-emerald-500 text-white rounded-2xl text-center" data-testid="referee-tournament-completed">
    <Trophy className="h-12 w-12 mx-auto mb-2" />
    <div className="text-xl font-bold">Tournament complete!</div>
    <div className="text-sm opacity-90 mt-1">Thank you for refereeing.</div>
  </div>
);

const AllCompleteBanner = () => (
  <div className="m-4 p-6 bg-emerald-50 border-2 border-emerald-300 text-emerald-900 rounded-2xl text-center" data-testid="referee-round-complete-banner">
    <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-emerald-600" />
    <div className="text-lg font-bold">All matches complete!</div>
    <div className="text-sm mt-1 opacity-80">Waiting for admin to generate the next round.</div>
  </div>
);

const ProgressBar = ({ scored, total, round, tnm, sampleMatch, groupStageOpen }) => (
  <div className="bg-white border-b border-slate-200 px-4 py-3" data-testid="referee-progress">
    <div className="flex items-center justify-between text-sm font-medium text-slate-700 mb-1">
      <span>{groupStageOpen ? 'Group Stage' : (tnm ? getRoundName(tnm, round, sampleMatch) : `Round ${round}`)}</span>
      <span data-testid="referee-progress-text">{scored} of {total} matches scored</span>
    </div>
    <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
      <div className="h-full bg-emerald-500 transition-all"
        style={{ width: total ? `${(scored / total) * 100}%` : '0%' }} />
    </div>
  </div>
);


// ─────────────────────────────────────────────────────────────
// Court section + Match card
// ─────────────────────────────────────────────────────────────
const groupByCourt = (matches) => {
  const out = {};
  for (const m of matches) {
    const k = m.court || 1;
    (out[k] = out[k] || []).push(m);
  }
  return out;
};

const isTiebreakerEnabled = (tnm) =>
  (tnm.sport === 'cricket' && tnm.cricket_tiebreaker === 'super_over') ||
  (tnm.sport === 'football' && tnm.football_tiebreaker === 'penalty_shootout');

// ─── Racquet Games → Sets → Match config resolver (mirror of admin) ───
const RACQUET_SPORTS = new Set(['padel', 'pickleball', 'tennis']);
const _refResolveStageKey = (tnm, match) => {
  if (match?.group) return 'group';
  const fmt = tnm?.format;
  if (fmt === 'round_robin') return 'round_robin';
  const total = Number(tnm?.total_rounds || tnm?.ko_rounds || 0);
  const rnd = Number(match?.round || 0);
  if (!total || !rnd) return 'default';
  const depthFromFinal = total - rnd;
  return ({ 0: 'final', 1: 'semis', 2: 'quarters', 3: 'r16', 4: 'r32' })[depthFromFinal] || `r${2 ** (depthFromFinal + 1)}`;
};
const resolveRacquetCfg = (tnm, match) => {
  if (!RACQUET_SPORTS.has(tnm?.sport)) return null;
  const byStage = tnm?.scoring_by_stage;
  if (!byStage) return null;
  const key = _refResolveStageKey(tnm, match);
  const cfg = byStage[key] || byStage.default;
  if (!cfg || cfg.mode !== 'games_sets_match') return null;
  return {
    best_of_sets: 3, games_target: 6, win_by_margin: 1,
    tiebreak_at: 6, tiebreak_target: 7, tiebreak_win_by: 2,
    ...cfg,
  };
};

const CourtSection = ({ court, matches, ...props }) => (
  <div data-testid={`referee-court-section-${court}`}>
    <h2 className="text-xl font-bold text-slate-900 mt-4 mb-2 px-1">Court {court}</h2>
    <div className="space-y-3">
      {matches.map(m => (
        <MatchCard key={m.id} match={m} {...props} />
      ))}
    </div>
  </div>
);

// Racquet Games → Sets → Match entry for referees. Mirrors admin
// `RacquetScoreModal` inline in the referee card. Sends `set_scores[]`
// to the same `/referee/matches/:id/score` endpoint the scalar path
// uses — backend `_apply_score` dispatches racquet vs legacy based on
// payload shape + tournament `scoring_by_stage`.
const RefRacquetMatchCard = ({ match, tournament, cfg, token, playerById, onScored }) => {
  const completed = match.status === 'completed';
  const bestOf = cfg.best_of_sets;
  const team1Label = resolveMatchSide({ playerIds: match.team1_player_ids, team: match.team1, teamId: match.team1_id }, playerById) || 'TBD';
  const team2Label = resolveMatchSide({ playerIds: match.team2_player_ids, team: match.team2, teamId: match.team2_id }, playerById) || 'TBD';
  const [sets, setSets] = useState(() => (
    (match.set_scores && match.set_scores.length)
      ? match.set_scores.map((s, i) => ({
          set_no: i + 1,
          side_a_games: s.side_a_games ?? '',
          side_b_games: s.side_b_games ?? '',
          side_a_tb: s.side_a_tb ?? '',
          side_b_tb: s.side_b_tb ?? '',
        }))
      : [{ set_no: 1, side_a_games: '', side_b_games: '', side_a_tb: '', side_b_tb: '' }]
  ));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const setField = (idx, key, val) =>
    setSets(prev => prev.map((s, i) => i === idx ? { ...s, [key]: val } : s));
  const addSet = () => sets.length < bestOf && setSets([...sets, {
    set_no: sets.length + 1, side_a_games: '', side_b_games: '', side_a_tb: '', side_b_tb: '',
  }]);
  const removeSet = (idx) => setSets(prev => prev
    .filter((_, i) => i !== idx)
    .map((s, i) => ({ ...s, set_no: i + 1 })));

  const submit = async () => {
    setErr('');
    const cleaned = sets.map(s => {
      const row = {
        set_no: s.set_no,
        side_a_games: parseInt(s.side_a_games, 10) || 0,
        side_b_games: parseInt(s.side_b_games, 10) || 0,
      };
      if (s.side_a_tb !== '' && s.side_a_tb != null) row.side_a_tb = parseInt(s.side_a_tb, 10) || 0;
      if (s.side_b_tb !== '' && s.side_b_tb != null) row.side_b_tb = parseInt(s.side_b_tb, 10) || 0;
      return row;
    });
    setSaving(true);
    try {
      const r = await apiFetch(`/referee/matches/${match.id}/score`, {
        method: 'POST',
        headers: { 'x-referee-token': token },
        body: JSON.stringify({ set_scores: cleaned }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setErr(j.detail || 'Submit failed');
        return;
      }
      onScored();
    } catch (e) {
      setErr('Network error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`rounded-2xl border p-4 ${completed ? 'bg-emerald-50/60 border-emerald-200 opacity-90' : 'bg-white border-slate-200'}`}
         data-testid={`referee-match-${match.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="text-xs text-slate-500 font-medium">
          Match #{match.match_number}
          {match.group && <span className="ml-2 text-slate-700">Group {match.group}</span>}
        </div>
        {completed && <CheckCircle2 className="h-5 w-5 text-emerald-600" data-testid={`referee-match-completed-${match.id}`} />}
      </div>

      <div className="text-[11px] text-indigo-700 mb-2">
        Best of {bestOf} · race to {cfg.games_target}
        {cfg.tiebreak_at != null
          ? ` · tiebreak at ${cfg.tiebreak_at}-${cfg.tiebreak_at} (first to ${cfg.tiebreak_target}, win by ${cfg.tiebreak_win_by})`
          : ` · win by ${cfg.win_by_margin}`}
      </div>

      <div className="text-xs font-medium text-slate-900 grid grid-cols-[1fr_auto_1fr] items-center gap-2 mb-2">
        <span className="text-right truncate">{team1Label}</span>
        <span className="px-2">—</span>
        <span className="truncate">{team2Label}</span>
      </div>

      {completed ? (
        <div className="text-sm text-center py-2 text-slate-700" data-testid={`referee-final-sets-${match.id}`}>
          {(match.set_scores || []).map((s, i) => (
            <span key={i} className="mx-1 font-mono">
              {s.side_a_games}-{s.side_b_games}
              {s.side_a_tb != null && s.side_b_tb != null && (
                <sup className="text-[9px] text-slate-500 ml-0.5">({s.side_a_tb}-{s.side_b_tb})</sup>
              )}
            </span>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {sets.map((s, i) => {
            const showTb = cfg.tiebreak_at != null
              && Number(s.side_a_games) === cfg.tiebreak_at
              && Number(s.side_b_games) === cfg.tiebreak_at;
            return (
              <div key={i} className="border rounded-lg p-2 space-y-1.5" data-testid={`ref-set-${match.id}-${i + 1}`}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-700 w-12">Set {s.set_no}</span>
                  <input type="number" inputMode="numeric" min="0" max="99"
                    value={s.side_a_games}
                    onChange={e => setField(i, 'side_a_games', e.target.value)}
                    className="w-14 h-11 text-center border rounded-lg text-lg font-bold"
                    data-testid={`ref-set-${match.id}-${i + 1}-a`} />
                  <span className="text-slate-400">—</span>
                  <input type="number" inputMode="numeric" min="0" max="99"
                    value={s.side_b_games}
                    onChange={e => setField(i, 'side_b_games', e.target.value)}
                    className="w-14 h-11 text-center border rounded-lg text-lg font-bold"
                    data-testid={`ref-set-${match.id}-${i + 1}-b`} />
                  {sets.length > 1 && (
                    <button type="button" onClick={() => removeSet(i)}
                      className="text-xs text-red-500 hover:underline ml-auto"
                      data-testid={`ref-set-${match.id}-${i + 1}-remove`}>remove</button>
                  )}
                </div>
                {showTb && (
                  <div className="flex items-center gap-2 pl-12" data-testid={`ref-tb-row-${match.id}-${i + 1}`}>
                    <span className="text-[11px] text-indigo-700">Tiebreak:</span>
                    <input type="number" inputMode="numeric" min="0" max="99"
                      value={s.side_a_tb}
                      onChange={e => setField(i, 'side_a_tb', e.target.value)}
                      placeholder="TB"
                      className="w-14 h-9 text-center border rounded-lg text-sm"
                      data-testid={`ref-tb-${match.id}-${i + 1}-a`} />
                    <span className="text-slate-400">—</span>
                    <input type="number" inputMode="numeric" min="0" max="99"
                      value={s.side_b_tb}
                      onChange={e => setField(i, 'side_b_tb', e.target.value)}
                      placeholder="TB"
                      className="w-14 h-9 text-center border rounded-lg text-sm"
                      data-testid={`ref-tb-${match.id}-${i + 1}-b`} />
                  </div>
                )}
              </div>
            );
          })}
          {sets.length < bestOf && (
            <button type="button" onClick={addSet}
              className="w-full py-2 border border-dashed rounded-lg text-xs text-slate-600 hover:bg-slate-50"
              data-testid={`ref-add-set-${match.id}`}>
              + Add set {sets.length + 1}
            </button>
          )}
        </div>
      )}

      {err && <div className="text-red-600 text-sm mt-2 text-center" data-testid={`referee-err-${match.id}`}>{err}</div>}

      <button
        onClick={submit}
        disabled={completed || saving}
        className={`mt-3 w-full h-12 rounded-2xl font-bold text-base transition active:scale-95 ${
          completed
            ? 'bg-emerald-100 text-emerald-700 cursor-default'
            : 'bg-blue-600 text-white disabled:bg-slate-300'
        }`}
        data-testid={`referee-submit-${match.id}`}
      >
        {completed ? (
          <span className="flex items-center justify-center gap-2"><CheckCircle2 className="h-5 w-5" /> Score submitted</span>
        ) : saving ? (
          <span className="flex items-center justify-center gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Submitting…</span>
        ) : (
          'Submit sets'
        )}
      </button>
    </div>
  );
};


const MatchCard = ({ match, format, tournament, pointsPerMatch, tiebreakerEnabled, token, playerById, onScored }) => {
  const racquetCfg = resolveRacquetCfg(tournament, match);
  if (racquetCfg) {
    return (
      <RefRacquetMatchCard
        match={match}
        tournament={tournament}
        cfg={racquetCfg}
        token={token}
        playerById={playerById}
        onScored={onScored}
      />
    );
  }
  const isIndividual = INDIVIDUAL_FORMATS.has(format);
  const completed = match.status === 'completed';

  // FIX 2: tiebreaker only applies to knockout-stage matches even if the
  // tournament-wide tiebreaker setting is on.
  const matchAllowsTiebreaker = tiebreakerEnabled && isKnockoutMatch(tournament, match);

  // For Americano/Mexicano, the total points per match are constrained
  const [s1, setS1] = useState('');
  const [s2, setS2] = useState('');
  const [tb1, setTb1] = useState('');
  const [tb2, setTb2] = useState('');
  const [showTb, setShowTb] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  // For americano/mexicano: auto-fill team2 = pointsPerMatch - team1 when team1 typed.
  // We keep both inputs visible but show the constraint hint.
  const isTie = s1 !== '' && s2 !== '' && parseInt(s1, 10) === parseInt(s2, 10);
  const needsTb = matchAllowsTiebreaker && isTie && !isIndividual;

  useEffect(() => {
    if (needsTb && !showTb) setShowTb(true);
    if (!needsTb && showTb) { setShowTb(false); setTb1(''); setTb2(''); }
  }, [needsTb, showTb]);

  const submit = async () => {
    setErr(null);
    if (s1 === '' || s2 === '') { setErr('Enter both scores'); return; }
    const n1 = parseInt(s1, 10), n2 = parseInt(s2, 10);
    if (isIndividual && (n1 + n2) !== pointsPerMatch) {
      setErr(`Scores must add up to ${pointsPerMatch}`);
      return;
    }
    if (needsTb && (tb1 === '' || tb2 === '')) {
      setErr('Enter tiebreaker scores'); return;
    }
    if (needsTb && parseInt(tb1, 10) === parseInt(tb2, 10)) {
      setErr('Tiebreaker cannot be a tie'); return;
    }
    setSubmitting(true);
    try {
      const body = {
        score_team1: n1,
        score_team2: n2,
      };
      if (needsTb) {
        body.tiebreaker_used = true;
        body.tiebreaker_score_team1 = parseInt(tb1, 10);
        body.tiebreaker_score_team2 = parseInt(tb2, 10);
      }
      const r = await apiFetch(`/referee/matches/${match.id}/score`, {
        method: 'POST',
        headers: { 'x-referee-token': token },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setErr(j.detail || 'Submit failed');
        return;
      }
      onScored();
    } catch (e) {
      setErr('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const team1Label = resolveMatchSide({ playerIds: match.team1_player_ids, team: match.team1, teamId: match.team1_id }, playerById) || 'TBD';
  const team2Label = resolveMatchSide({ playerIds: match.team2_player_ids, team: match.team2, teamId: match.team2_id }, playerById) || 'TBD';

  return (
    <div className={`rounded-2xl border p-4 ${completed ? 'bg-emerald-50/60 border-emerald-200 opacity-90' : 'bg-white border-slate-200'}`} data-testid={`referee-match-${match.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="text-xs text-slate-500 font-medium">
          Match #{match.match_number}
          {match.group && <span className="ml-2 text-slate-700">Group {match.group}</span>}
        </div>
        {completed && <CheckCircle2 className="h-5 w-5 text-emerald-600" data-testid={`referee-match-completed-${match.id}`} />}
      </div>

      {/* Teams + scores */}
      <div className="space-y-3">
        <TeamRow name={team1Label} score={completed ? match.score_team1 : s1}
          setScore={setS1} disabled={completed} winner={completed && match.winner_id === match.team1_id}
          testid={`referee-team1-${match.id}`} />
        <div className="text-center text-xs text-slate-400 font-semibold">VS</div>
        <TeamRow name={team2Label} score={completed ? match.score_team2 : s2}
          setScore={setS2} disabled={completed} winner={completed && match.winner_id === match.team2_id}
          testid={`referee-team2-${match.id}`} />
      </div>

      {isIndividual && !completed && (
        <div className="text-[11px] text-slate-500 text-center mt-2">
          Total points per match: <strong className="text-slate-700">{pointsPerMatch}</strong>
        </div>
      )}

      {showTb && !completed && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid={`referee-tiebreaker-${match.id}`}>
          <div className="text-xs font-semibold text-amber-900 mb-2">Tiebreaker required</div>
          <div className="grid grid-cols-2 gap-2">
            <input type="number" inputMode="numeric" pattern="[0-9]*" placeholder="TB"
              value={tb1} onChange={e => setTb1(e.target.value)}
              className="px-3 py-2 border rounded-lg text-center text-lg font-bold"
              data-testid={`referee-tb1-${match.id}`} />
            <input type="number" inputMode="numeric" pattern="[0-9]*" placeholder="TB"
              value={tb2} onChange={e => setTb2(e.target.value)}
              className="px-3 py-2 border rounded-lg text-center text-lg font-bold"
              data-testid={`referee-tb2-${match.id}`} />
          </div>
        </div>
      )}

      {err && <div className="text-red-600 text-sm mt-2 text-center" data-testid={`referee-err-${match.id}`}>{err}</div>}

      <button
        onClick={submit}
        disabled={completed || submitting || s1 === '' || s2 === ''}
        className={`mt-4 w-full h-14 rounded-2xl font-bold text-base transition active:scale-95 ${
          completed
            ? 'bg-emerald-100 text-emerald-700 cursor-default'
            : 'bg-blue-600 text-white disabled:bg-slate-300'
        }`}
        data-testid={`referee-submit-${match.id}`}
      >
        {completed ? (
          <span className="flex items-center justify-center gap-2"><CheckCircle2 className="h-5 w-5" /> Score submitted</span>
        ) : submitting ? (
          <span className="flex items-center justify-center gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Submitting…</span>
        ) : (
          'Submit score'
        )}
      </button>

      {/* Match report — football only, ONLY after the score is submitted */}
      {completed && tournament?.sport === 'football' && (
        <RefereeMatchReport match={match} token={token} onChanged={onScored} />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Referee Match Report (football) — appears below a completed match
// ─────────────────────────────────────────────────────────────
const blankGoal = () => ({ player_name: '', count: 1 });
const blankCard = (teamId) => ({ player_name: '', team_id: teamId });

const RefereeMatchReport = ({ match, token, onChanged }) => {
  const hasReport = !!match.match_report;
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(hasReport ? 'submitted' : null); // 'submitted' | 'skipped' | null
  const [submitting, setSubmitting] = useState(false);
  const [warning, setWarning] = useState(null);
  const [err, setErr] = useState(null);

  const t1 = match.team1 || {};
  const t2 = match.team2 || {};
  const t1Id = match.team1_id;
  const t2Id = match.team2_id;
  const t1Squad = (t1.players || []).map(p => p.name).filter(Boolean);
  const t2Squad = (t2.players || []).map(p => p.name).filter(Boolean);
  const squadByTeam = { [t1Id]: t1Squad, [t2Id]: t2Squad };

  const seed = (rep) => ({
    g1: rep ? rep.goals.filter(g => g.team_id === t1Id).map(g => ({ player_name: g.player_name, count: g.count || 1 })) : [],
    g2: rep ? rep.goals.filter(g => g.team_id === t2Id).map(g => ({ player_name: g.player_name, count: g.count || 1 })) : [],
    yellows: rep ? rep.yellow_cards.map(c => ({ player_name: c.player_name, team_id: c.team_id })) : [],
    reds: rep ? rep.red_cards.map(c => ({ player_name: c.player_name, team_id: c.team_id })) : [],
  });
  const initial = seed(match.match_report);
  const [g1, setG1] = useState(initial.g1);
  const [g2, setG2] = useState(initial.g2);
  const [yellows, setYellows] = useState(initial.yellows);
  const [reds, setReds] = useState(initial.reds);

  // Resolve player_id from squad by exact name match
  const resolveId = (teamId, name) => {
    const team = teamId === t1Id ? t1 : t2;
    const hit = (team.players || []).find(p => (p.name || '').trim().toLowerCase() === (name || '').trim().toLowerCase());
    return hit?.id || null;
  };

  const buildBody = () => {
    const goals = [];
    g1.filter(r => r.player_name.trim()).forEach(r => goals.push({ player_name: r.player_name.trim(), player_id: resolveId(t1Id, r.player_name), team_id: t1Id, count: Math.max(1, parseInt(r.count, 10) || 1) }));
    g2.filter(r => r.player_name.trim()).forEach(r => goals.push({ player_name: r.player_name.trim(), player_id: resolveId(t2Id, r.player_name), team_id: t2Id, count: Math.max(1, parseInt(r.count, 10) || 1) }));
    const yellow_cards = yellows.filter(c => c.player_name.trim()).map(c => ({ player_name: c.player_name.trim(), player_id: resolveId(c.team_id, c.player_name), team_id: c.team_id }));
    const red_cards = reds.filter(c => c.player_name.trim()).map(c => ({ player_name: c.player_name.trim(), player_id: resolveId(c.team_id, c.player_name), team_id: c.team_id }));
    return { goals, yellow_cards, red_cards };
  };

  const submit = async () => {
    setErr(null); setWarning(null); setSubmitting(true);
    try {
      const r = await apiFetch(`/referee/matches/${match.id}/report`, {
        method: 'POST',
        headers: { 'x-referee-token': token },
        body: JSON.stringify(buildBody()),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(j.detail || 'Could not submit report'); return; }
      if (j.warning) setWarning(j.warning);
      setDone('submitted');
      setOpen(false);
      onChanged && onChanged();
    } catch (e) {
      setErr('Network error');
    } finally { setSubmitting(false); }
  };

  // Collapsed indicator (after submit / skip, or before opening)
  if (!open) {
    return (
      <div className="mt-3" data-testid={`referee-report-collapsed-${match.id}`}>
        {done === 'submitted' ? (
          <button onClick={() => setOpen(true)}
            className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl py-2.5"
            data-testid={`referee-report-submitted-${match.id}`}>
            <CheckCircle2 className="h-4 w-4" /> Report submitted · Edit
          </button>
        ) : done === 'skipped' ? (
          <button onClick={() => { setDone(null); setOpen(true); }}
            className="w-full flex items-center justify-center gap-2 text-sm font-medium text-slate-500 bg-slate-50 border border-slate-200 rounded-xl py-2.5"
            data-testid={`referee-report-skipped-${match.id}`}>
            Skipped · Add report
          </button>
        ) : (
          <button onClick={() => setOpen(true)}
            className="w-full flex items-center justify-center gap-2 text-sm font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-xl py-3"
            data-testid={`referee-add-report-${match.id}`}>
            <ClipboardList className="h-4 w-4" /> Add Match Report
          </button>
        )}
        {warning && (
          <div className="mt-2 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid={`referee-report-warning-${match.id}`}>
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {warning}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-2xl border border-blue-200 bg-blue-50/40 p-3" data-testid={`referee-report-form-${match.id}`}>
      <div className="text-xs text-slate-500 mb-3">Optional: add goal scorers and cards for this match</div>

      <GoalScorerTeam label={t1.team_name || 'Team A'} rows={g1} setRows={setG1} suggestions={t1Squad} testid={`report-goals-t1-${match.id}`} />
      <GoalScorerTeam label={t2.team_name || 'Team B'} rows={g2} setRows={setG2} suggestions={t2Squad} testid={`report-goals-t2-${match.id}`} />

      <CardSection title="Yellow cards" color="amber" rows={yellows} setRows={setYellows}
        t1={{ id: t1Id, name: t1.team_name || 'Team A' }} t2={{ id: t2Id, name: t2.team_name || 'Team B' }}
        squadByTeam={squadByTeam} defaultTeam={t1Id} testid={`report-yellow-${match.id}`} />
      <CardSection title="Red cards" color="red" rows={reds} setRows={setReds}
        t1={{ id: t1Id, name: t1.team_name || 'Team A' }} t2={{ id: t2Id, name: t2.team_name || 'Team B' }}
        squadByTeam={squadByTeam} defaultTeam={t1Id} testid={`report-red-${match.id}`} />

      {err && <div className="text-red-600 text-sm mt-2" data-testid={`referee-report-err-${match.id}`}>{err}</div>}

      <button onClick={submit} disabled={submitting}
        className="mt-3 w-full h-13 py-3.5 rounded-2xl bg-blue-600 text-white font-bold text-base disabled:bg-slate-300 active:scale-95 transition"
        data-testid={`referee-report-submit-${match.id}`}>
        {submitting ? <span className="flex items-center justify-center gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Submitting…</span> : 'Submit Report'}
      </button>
      <button onClick={() => { setDone(hasReport ? 'submitted' : 'skipped'); setOpen(false); }}
        className="mt-2 w-full text-sm text-slate-500 font-medium py-2"
        data-testid={`referee-report-skip-${match.id}`}>
        Skip — no report needed
      </button>
    </div>
  );
};

const GoalScorerTeam = ({ label, rows, setRows, suggestions, testid }) => {
  const update = (i, patch) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const remove = (i) => setRows(rows.filter((_, idx) => idx !== i));
  return (
    <div className="mb-3" data-testid={testid}>
      <div className="text-sm font-bold text-slate-800 mb-1.5">⚽ {label}</div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i}>
            <div className="flex items-center gap-2">
              <input value={r.player_name} onChange={e => update(i, { player_name: e.target.value })}
                placeholder="Player name"
                className="flex-1 min-w-0 px-3 py-2.5 rounded-xl border border-slate-300 text-base focus:border-blue-500 focus:outline-none"
                data-testid={`${testid}-name-${i}`} />
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => update(i, { count: Math.max(1, (parseInt(r.count, 10) || 1) - 1) })}
                  className="h-9 w-9 rounded-lg bg-white border border-slate-300 flex items-center justify-center active:scale-95" aria-label="decrease goals"
                  data-testid={`${testid}-minus-${i}`}><Minus className="h-4 w-4" /></button>
                <span className="w-7 text-center font-bold text-slate-800" data-testid={`${testid}-count-${i}`}>{r.count}</span>
                <button onClick={() => update(i, { count: (parseInt(r.count, 10) || 1) + 1 })}
                  className="h-9 w-9 rounded-lg bg-white border border-slate-300 flex items-center justify-center active:scale-95" aria-label="increase goals"
                  data-testid={`${testid}-plus-${i}`}><Plus className="h-4 w-4" /></button>
              </div>
              <button onClick={() => remove(i)} className="h-9 w-9 rounded-lg text-slate-400 flex items-center justify-center shrink-0" aria-label="remove scorer"
                data-testid={`${testid}-remove-${i}`}><X className="h-4 w-4" /></button>
            </div>
            {suggestions.length > 0 && !r.player_name && (
              <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`${testid}-suggestions-${i}`}>
                {suggestions.map((name, si) => (
                  <button key={si} onClick={() => update(i, { player_name: name })}
                    className="text-xs px-2.5 py-1 rounded-full bg-white border border-blue-200 text-blue-700 active:scale-95"
                    data-testid={`${testid}-suggestion-${i}-${si}`}>{name}</button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <button onClick={() => setRows([...rows, blankGoal()])}
        className="mt-2 flex items-center gap-1 text-sm font-semibold text-blue-700"
        data-testid={`${testid}-add`}><Plus className="h-4 w-4" /> Add scorer</button>
    </div>
  );
};

const CardSection = ({ title, color, rows, setRows, t1, t2, squadByTeam, defaultTeam, testid }) => {
  const update = (i, patch) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const remove = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const dot = color === 'red' ? 'bg-red-500' : 'bg-amber-400';
  return (
    <div className="mb-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-sm font-bold text-slate-800 mb-1.5">
        <span className={`inline-block w-3 h-4 rounded-sm ${dot}`} /> {title}
      </div>
      <div className="space-y-2">
        {rows.map((r, i) => {
          const sug = squadByTeam[r.team_id] || [];
          return (
            <div key={i}>
              <div className="flex items-center gap-2">
                <input value={r.player_name} onChange={e => update(i, { player_name: e.target.value })}
                  placeholder="Player name"
                  className="flex-1 min-w-0 px-3 py-2.5 rounded-xl border border-slate-300 text-base focus:border-blue-500 focus:outline-none"
                  data-testid={`${testid}-name-${i}`} />
                <select value={r.team_id} onChange={e => update(i, { team_id: e.target.value })}
                  className="shrink-0 px-2 py-2.5 rounded-xl border border-slate-300 text-sm bg-white max-w-[40%] truncate"
                  data-testid={`${testid}-team-${i}`}>
                  <option value={t1.id}>{t1.name}</option>
                  <option value={t2.id}>{t2.name}</option>
                </select>
                <button onClick={() => remove(i)} className="h-9 w-9 rounded-lg text-slate-400 flex items-center justify-center shrink-0" aria-label="remove card"
                  data-testid={`${testid}-remove-${i}`}><X className="h-4 w-4" /></button>
              </div>
              {sug.length > 0 && !r.player_name && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {sug.map((name, si) => (
                    <button key={si} onClick={() => update(i, { player_name: name })}
                      className="text-xs px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-600 active:scale-95"
                      data-testid={`${testid}-suggestion-${i}-${si}`}>{name}</button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <button onClick={() => setRows([...rows, blankCard(defaultTeam)])}
        className="mt-2 flex items-center gap-1 text-sm font-semibold text-blue-700"
        data-testid={`${testid}-add`}><Plus className="h-4 w-4" /> Add card</button>
    </div>
  );
};

const TeamRow = ({ name, score, setScore, disabled, winner, testid }) => (
  <div className={`flex items-center gap-3 ${winner ? '' : ''}`}>
    <div className={`flex-1 text-lg font-semibold leading-tight ${winner ? 'text-emerald-700' : 'text-slate-900'}`} data-testid={`${testid}-name`}>
      {name}
    </div>
    <input
      type="number" inputMode="numeric" pattern="[0-9]*" min="0"
      value={score ?? ''} onChange={e => setScore && setScore(e.target.value)} disabled={disabled}
      className={`w-20 h-16 rounded-xl border-2 text-center text-3xl font-bold ${
        disabled ? 'bg-slate-50 border-slate-200 text-slate-500' : 'border-slate-300 focus:border-blue-500 focus:outline-none'
      }`}
      data-testid={`${testid}-score`}
    />
  </div>
);
